import React, { useEffect, useState } from 'react';
import { AlertTriangle, Loader2, ShieldCheck } from 'lucide-react';
import { Badge } from '@roger/ui-components';
import { screenCandidateMedication } from '../../services/api';
import type { InteractionScreen } from '../../services/api';
import {
  ALLERGY_BADGE_CLASSES,
  SEVERITY_BADGE_CLASSES,
  SEVERITY_LABELS,
} from './screeningStyles';

interface CandidateScreenAlertProps {
  patientId: string;
  /** FDB id of the drug being considered; null hides the alert entirely. */
  fdbDispensableDrugId: string | null;
}

type AlertStatus = 'idle' | 'loading' | 'ready' | 'error';

/**
 * Non-blocking interaction preview for a drug selected in the picker but not
 * yet assigned. Screens the candidate against the patient's current
 * medications and allergies in the background; findings are advisory — saving
 * is never gated, and the authoritative screen still runs at signing.
 */
const CandidateScreenAlert: React.FC<CandidateScreenAlertProps> = ({
  patientId,
  fdbDispensableDrugId,
}) => {
  const [screen, setScreen] = useState<InteractionScreen | null>(null);
  const [status, setStatus] = useState<AlertStatus>('idle');

  useEffect(() => {
    if (!fdbDispensableDrugId) {
      setScreen(null);
      setStatus('idle');
      return;
    }
    let cancelled = false;
    setStatus('loading');
    (async () => {
      try {
        const response = await screenCandidateMedication({
          path: { patient_id: patientId },
          body: { fdb_dispensable_drug_id: fdbDispensableDrugId },
        });
        if (cancelled) return;
        if (response.error || !response.data) {
          console.error('Candidate screening failed:', response.error);
          setStatus('error');
          return;
        }
        setScreen(response.data);
        setStatus('ready');
      } catch (error) {
        if (!cancelled) {
          console.error('Candidate screening failed:', error);
          setStatus('error');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [patientId, fdbDispensableDrugId]);

  if (!fdbDispensableDrugId || status === 'idle') return null;

  if (status === 'loading') {
    return (
      <div className='flex items-center gap-2 text-xs text-muted-foreground'>
        <Loader2 className='h-3 w-3 animate-spin' />
        Checking interactions…
      </div>
    );
  }

  if (status === 'error' || screen?.unavailable) {
    return (
      <div className='flex items-center gap-2 text-xs text-amber-700'>
        <AlertTriangle className='h-3.5 w-3.5 flex-shrink-0' />
        Interaction check unavailable — screening runs again at signing.
      </div>
    );
  }

  const ddi = screen?.ddi ?? [];
  const da = screen?.da ?? [];

  if (ddi.length === 0 && da.length === 0) {
    return (
      <div className='flex items-center gap-2 text-xs text-muted-foreground'>
        <ShieldCheck className='h-3.5 w-3.5 text-green-600' />
        No interactions found with current medications and allergies.
      </div>
    );
  }

  return (
    <div className='rounded-md border border-amber-300 bg-amber-50 px-3 py-2 space-y-1.5'>
      {ddi.map((finding, index) => {
        const code =
          finding.severity && SEVERITY_LABELS[finding.severity]
            ? finding.severity
            : '9';
        return (
          <div
            key={finding.interaction_id ?? `ddi-${index}`}
            className='flex items-start gap-2 text-sm'
          >
            <Badge
              className={`text-xs flex-shrink-0 ${SEVERITY_BADGE_CLASSES[code]}`}
            >
              {SEVERITY_LABELS[code]}
            </Badge>
            <span>
              {finding.screen_message ||
                finding.interaction_desc ||
                'Interaction identified.'}
            </span>
          </div>
        );
      })}
      {da.map((finding, index) => (
        <div
          key={finding.allergen_id ?? `da-${index}`}
          className='flex items-start gap-2 text-sm'
        >
          <Badge className={`text-xs flex-shrink-0 ${ALLERGY_BADGE_CLASSES}`}>
            Allergy
          </Badge>
          <span>
            {finding.screen_message ||
              finding.allergen_desc ||
              'Allergy interaction identified.'}
          </span>
        </div>
      ))}
      <p className='text-xs text-amber-700'>
        Review before assigning — saving is not blocked.
      </p>
    </div>
  );
};

export default CandidateScreenAlert;
